// Synthesized meeting-alert chimes via the Web Audio API.
//
// We generate tones at runtime instead of shipping audio assets: no extra
// network fetch, works offline, and lets us tune two clearly distinct alerts.
//   - T-10 min: a calm two-note rising chime (heads-up).
//   - T-1 min:  an urgent triple beep (act now).
//
// Browsers suspend/forbid AudioContext until a user gesture, so callers must
// invoke unlockAlertAudio() once from a real interaction (pointer/keydown).

const LOG_PREFIX = '[MeetingAlertSounds]'

let _ctx = null

/** Lazily create the shared AudioContext. Returns null if unsupported. */
function getCtx() {
  if (_ctx) return _ctx
  try {
    const Ctx = window.AudioContext || window.webkitAudioContext
    if (!Ctx) return null
    _ctx = new Ctx()
  } catch (e) {
    console.warn(LOG_PREFIX, 'AudioContext unavailable', { error: String(e?.message ?? e) })
    _ctx = null
  }
  return _ctx
}

/**
 * Resume the AudioContext from a user gesture so later scheduled chimes can
 * play. Safe to call repeatedly; cheap no-op once running.
 */
export function unlockAlertAudio() {
  const ctx = getCtx()
  if (ctx && ctx.state === 'suspended') {
    ctx.resume().catch(() => { /* ignore — will retry on next gesture */ })
  }
}

/**
 * Play a single tone with a short attack/release envelope to avoid clicks.
 * @param {AudioContext} ctx
 * @param {number} freq   frequency in Hz
 * @param {number} start  start time (ctx.currentTime-relative seconds)
 * @param {number} dur    duration in seconds
 * @param {object} [opts]
 * @param {OscillatorType} [opts.type='sine']
 * @param {number} [opts.gain=0.18] peak gain (0–1)
 */
function tone(ctx, freq, start, dur, opts = {}) {
  const { type = 'sine', gain = 0.18 } = opts
  const osc = ctx.createOscillator()
  const env = ctx.createGain()
  osc.type = type
  osc.frequency.value = freq

  const t0 = ctx.currentTime + start
  const attack = 0.012
  const release = 0.08
  env.gain.setValueAtTime(0.0001, t0)
  env.gain.exponentialRampToValueAtTime(gain, t0 + attack)
  env.gain.setValueAtTime(gain, t0 + Math.max(attack, dur - release))
  env.gain.exponentialRampToValueAtTime(0.0001, t0 + dur)

  osc.connect(env)
  env.connect(ctx.destination)
  osc.start(t0)
  osc.stop(t0 + dur + 0.02)
}

/** T-10 min: calm two-note rising chime (E5 → A5), gentle sine. */
export function playTenMinuteChime() {
  const ctx = getCtx()
  if (!ctx) return
  const run = () => {
    tone(ctx, 659.25, 0.0, 0.22, { type: 'sine', gain: 0.16 })
    tone(ctx, 880.0, 0.22, 0.34, { type: 'sine', gain: 0.16 })
  }
  if (ctx.state === 'suspended') ctx.resume().then(run).catch(() => {})
  else run()
}

/** T-1 min: urgent triple beep (B5 x3), brighter triangle, louder. */
export function playOneMinuteChime() {
  const ctx = getCtx()
  if (!ctx) return
  const run = () => {
    const f = 987.77
    tone(ctx, f, 0.0, 0.13, { type: 'triangle', gain: 0.24 })
    tone(ctx, f, 0.18, 0.13, { type: 'triangle', gain: 0.24 })
    tone(ctx, f, 0.36, 0.18, { type: 'triangle', gain: 0.24 })
  }
  if (ctx.state === 'suspended') ctx.resume().then(run).catch(() => {})
  else run()
}
